import {
  ActivityIcon,
  CircleDotIcon,
  DropletIcon,
  GaugeIcon,
  SettingsIcon,
  SnowflakeIcon,
  TimerIcon,
  TruckIcon,
  WrenchIcon,
  type LucideIcon,
} from 'lucide-react'
import type { L } from '../types/i18n'

export interface Service {
  slug: string
  name: L
  icon: LucideIcon
  short: L
  description: L
  includes: L[]
  price?: L
  featured: boolean
  image?: string
}

export const services: Service[] = [
  {
    slug: 'diagnostics',
    name: { en: 'Diagnostic Services', es: 'Servicios de Diagnóstico' },
    icon: ActivityIcon,
    short: {
      en: 'Accurate vehicle diagnostics using professional equipment. We identify the problem, explain what’s needed, and show you the price.',
      es: 'Diagnóstico preciso de vehículos con equipos profesionales. Identificamos el problema, explicamos qué se necesita y te mostramos el precio.',
    },
    description: {
      en: 'Our diagnostic bay is the heart of the shop. We pull codes with professional scan tools, then verify the real cause by hand before recommending a single part. You get a plain-English explanation of what is wrong and what it costs.',
      es: 'Nuestra bahía de diagnóstico es el corazón del taller. Leemos los códigos con escáneres profesionales y luego verificamos la causa real a mano antes de recomendar una sola pieza. Recibes una explicación en lenguaje claro de qué está mal y cuánto cuesta.',
    },
    includes: [
      {
        en: 'Check engine & warning light diagnosis',
        es: 'Diagnóstico de luz de motor y testigos',
      },
      {
        en: 'Electrical and charging system testing',
        es: 'Pruebas del sistema eléctrico y de carga',
      },
      {
        en: 'Drivability, misfire and stalling issues',
        es: 'Problemas de manejo, fallos de encendido y apagones',
      },
      {
        en: 'Pre-purchase inspections',
        es: 'Inspecciones antes de comprar un vehículo',
      },
    ],
    price: { en: 'Starting at $89', es: 'A partir de $89' },
    featured: true,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3c1ef5bd-9641-423c-8b9b-7aa486aff19c.jpg',
  },
  {
    slug: 'brakes',
    name: {
      en: 'Brake Service & Repair',
      es: 'Servicio y Reparación de Frenos',
    },
    icon: CircleDotIcon,
    short: {
      en: 'Complete brake system inspection, repair, and replacement. Safety is non-negotiable.',
      es: 'Inspección completa, reparación y reemplazo del sistema de frenos. La seguridad no es negociable.',
    },
    description: {
      en: 'Squealing, grinding, or a soft pedal are all worth a same-day look. We inspect the full system — pads, rotors, calipers, hoses and fluid — and show you the measurements so you can see exactly how much life is left.',
      es: 'Chillidos, rechinidos o un pedal suave merecen una revisión el mismo día. Inspeccionamos todo el sistema — pastillas, discos, cálipers, mangueras y líquido — y te mostramos las medidas para que veas exactamente cuánta vida les queda.',
    },
    includes: [
      {
        en: 'Pad and rotor replacement',
        es: 'Reemplazo de pastillas y discos',
      },
      {
        en: 'Caliper and brake hose repair',
        es: 'Reparación de cálipers y mangueras',
      },
      { en: 'Brake fluid flush', es: 'Cambio de líquido de frenos' },
      {
        en: 'ABS warning light diagnosis',
        es: 'Diagnóstico de la luz de ABS',
      },
    ],
    price: { en: 'Starting at $150', es: 'A partir de $150' },
    featured: true,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/05460e85-5096-45a8-83e8-64eb38a8b3ec.jpg',
  },
  {
    slug: 'suspension',
    name: { en: 'Suspension & Struts', es: 'Suspensión y Amortiguadores' },
    icon: SettingsIcon,
    short: {
      en: 'Professional struts, shocks, and suspension service. Better ride, safer handling.',
      es: 'Servicio profesional de amortiguadores y suspensión. Mejor conducción, manejo más seguro.',
    },
    description: {
      en: 'A bouncy ride, uneven tire wear or clunking over bumps usually points to worn struts or bushings. We replace them with quality components so the car tracks straight and your tires stop wearing out early.',
      es: 'Una conducción brincona, desgaste irregular de llantas o golpeteos en los baches suelen indicar amortiguadores o bujes desgastados. Los reemplazamos con componentes de calidad para que el auto vaya derecho y tus llantas dejen de gastarse antes de tiempo.',
    },
    includes: [
      {
        en: 'Strut and shock replacement',
        es: 'Reemplazo de amortiguadores',
      },
      {
        en: 'Control arms, ball joints and bushings',
        es: 'Horquillas, rótulas y bujes',
      },
      {
        en: 'Tie rods and steering components',
        es: 'Terminales y componentes de dirección',
      },
      {
        en: 'Ride height and handling inspection',
        es: 'Inspección de altura y manejo',
      },
    ],
    price: { en: 'Starting at $200', es: 'A partir de $200' },
    featured: true,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8c5f46e6-89f9-4bdf-a669-f7d04d940de2.jpg',
  },
  {
    slug: 'tune-up',
    name: { en: 'Complete Tune-Ups', es: 'Afinaciones Completas' },
    icon: GaugeIcon,
    short: {
      en: 'Full vehicle tune-ups for optimal performance and fuel efficiency.',
      es: 'Afinaciones completas del vehículo para rendimiento y eficiencia de combustible óptimos.',
    },
    description: {
      en: 'Rough idle, weak acceleration or falling fuel economy are usually fixable with a proper tune-up. We service the ignition and fuel side of the engine and confirm the improvement on the scan tool before you leave.',
      es: 'Marcha irregular, aceleración débil o consumo de combustible en aumento casi siempre se corrigen con una afinación adecuada. Atendemos el encendido y la alimentación de combustible, y confirmamos la mejora con el escáner antes de que te vayas.',
    },
    includes: [
      {
        en: 'Spark plugs and ignition coils',
        es: 'Bujías y bobinas de encendido',
      },
      {
        en: 'Air and fuel filter replacement',
        es: 'Cambio de filtros de aire y combustible',
      },
      {
        en: 'Throttle body and induction service',
        es: 'Servicio de cuerpo de aceleración e inducción',
      },
      {
        en: 'Fuel economy and performance check',
        es: 'Revisión de rendimiento y consumo',
      },
    ],
    price: { en: 'Starting at $100', es: 'A partir de $100' },
    featured: true,
  },
  {
    slug: 'oil-change',
    name: {
      en: 'Oil Changes & Maintenance',
      es: 'Cambios de Aceite y Mantenimiento',
    },
    icon: DropletIcon,
    short: {
      en: 'Quality oil changes and routine scheduled maintenance to keep your vehicle running smoothly.',
      es: 'Cambios de aceite de calidad y mantenimiento rutinario programado para mantener tu vehículo funcionando sin problemas.',
    },
    description: {
      en: 'Fast, done right, and never used as a sales pitch. We use the correct oil weight and a quality filter for your engine, top off the fluids, and give you an honest heads-up on anything worth watching — no pressure to fix it today.',
      es: 'Rápido, bien hecho y nunca usado como pretexto de venta. Usamos el aceite correcto y un filtro de calidad para tu motor, revisamos los fluidos y te avisamos con honestidad de cualquier detalle a vigilar — sin presión para arreglarlo hoy.',
    },
    includes: [
      {
        en: 'Conventional, blend and full synthetic oil',
        es: 'Aceite convencional, semisintético y sintético',
      },
      {
        en: 'Filter replacement and fluid top-off',
        es: 'Cambio de filtro y relleno de fluidos',
      },
      {
        en: 'Multi-point vehicle inspection',
        es: 'Inspección de múltiples puntos',
      },
      {
        en: 'Manufacturer maintenance schedules',
        es: 'Mantenimiento según el fabricante',
      },
    ],
    price: { en: 'Starting at $45', es: 'A partir de $45' },
    featured: true,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/37271986-eef5-4029-aa55-ae05eaf6f722.jpg',
  },
  {
    slug: 'ac-service',
    name: { en: 'A/C Service', es: 'Servicio de A/C' },
    icon: SnowflakeIcon,
    short: {
      en: 'Professional air conditioning diagnosis, repair, and recharge. Stay cool.',
      es: 'Diagnóstico, reparación y recarga profesional de aire acondicionado. Mantente fresco.',
    },
    description: {
      en: 'Blowing warm is almost always a leak, not just low refrigerant. We find the leak, repair it, then evacuate and recharge the system to spec so the cold air is still there next summer.',
      es: 'Si sale aire caliente, casi siempre hay una fuga y no solo falta de refrigerante. Encontramos la fuga, la reparamos y luego vaciamos y recargamos el sistema a especificación para que el aire frío siga ahí el próximo verano.',
    },
    includes: [
      {
        en: 'Leak detection and pressure testing',
        es: 'Detección de fugas y prueba de presión',
      },
      {
        en: 'Compressor and condenser replacement',
        es: 'Reemplazo de compresor y condensador',
      },
      {
        en: 'Evacuate and recharge to specification',
        es: 'Vaciado y recarga a especificación',
      },
      {
        en: 'Heater and blower motor repair',
        es: 'Reparación de calefacción y motor del ventilador',
      },
    ],
    price: { en: 'Starting at $120', es: 'A partir de $120' },
    featured: true,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ecf5899a-f6fb-4e91-8f0f-fcdef694f3be.jpg',
  },
  {
    slug: 'timing-belt',
    name: {
      en: 'Timing Belt Replacement',
      es: 'Cambio de Correa de Distribución',
    },
    icon: TimerIcon,
    short: {
      en: 'Timing belt, tensioner and water pump replacement on schedule.',
      es: 'Cambio de correa de distribución, tensor y bomba de agua a tiempo.',
    },
    description: {
      en: 'This is maintenance you do not postpone — a failed belt can destroy an engine. We replace the belt along with the tensioner, idlers and water pump while the front of the engine is open, so you only pay the labor once.',
      es: 'Este es un mantenimiento que no conviene posponer — una correa rota puede destruir el motor. Cambiamos la correa junto con el tensor, las poleas y la bomba de agua mientras el frente del motor está abierto, así pagas la mano de obra una sola vez.',
    },
    includes: [
      {
        en: 'Timing belt and tensioner replacement',
        es: 'Cambio de correa y tensor',
      },
      {
        en: 'Water pump and idler pulleys',
        es: 'Bomba de agua y poleas',
      },
      {
        en: 'Serpentine belt inspection',
        es: 'Inspección de la banda serpentina',
      },
      {
        en: 'Manufacturer interval verification',
        es: 'Verificación del intervalo del fabricante',
      },
    ],
    featured: false,
  },
  {
    slug: 'general-maintenance',
    name: {
      en: 'General Repair & Maintenance',
      es: 'Reparación y Mantenimiento General',
    },
    icon: WrenchIcon,
    short: {
      en: 'Cooling, exhaust, batteries, starters, alternators and more.',
      es: 'Enfriamiento, escape, baterías, marchas, alternadores y más.',
    },
    description: {
      en: 'If it keeps your car on the road, we work on it. Customers often come to us with jobs other shops turned down — bring us the problem and we will tell you honestly whether we can fix it.',
      es: 'Si mantiene tu auto en la carretera, lo atendemos. Muchos clientes llegan con trabajos que otros talleres rechazaron — tráenos el problema y te diremos con honestidad si podemos arreglarlo.',
    },
    includes: [
      {
        en: 'Batteries, starters and alternators',
        es: 'Baterías, marchas y alternadores',
      },
      {
        en: 'Radiators, hoses and cooling system',
        es: 'Radiadores, mangueras y sistema de enfriamiento',
      },
      {
        en: 'Exhaust and emissions repair',
        es: 'Reparación de escape y emisiones',
      },
      {
        en: 'Belts, pulleys and engine accessories',
        es: 'Bandas, poleas y accesorios del motor',
      },
    ],
    featured: false,
  },
  {
    slug: 'fleet-service',
    name: { en: 'Small Fleet Service', es: 'Servicio para Flotas Pequeñas' },
    icon: TruckIcon,
    short: {
      en: 'Scheduled maintenance for work vans, trucks and small fleets.',
      es: 'Mantenimiento programado para camionetas de trabajo, troques y flotas pequeñas.',
    },
    description: {
      en: 'Local contractors and small businesses rely on us to keep their vehicles working. We set up a maintenance schedule, prioritize downtime, and keep records so you know exactly what was done and when.',
      es: 'Contratistas locales y pequeños negocios confían en nosotros para mantener sus vehículos trabajando. Armamos un calendario de mantenimiento, priorizamos el tiempo fuera de servicio y llevamos registros para que sepas exactamente qué se hizo y cuándo.',
    },
    includes: [
      {
        en: 'Preventive maintenance scheduling',
        es: 'Programación de mantenimiento preventivo',
      },
      {
        en: 'Priority turnaround on downtime',
        es: 'Atención prioritaria para reducir paros',
      },
      {
        en: 'Consolidated invoicing and records',
        es: 'Facturación y registros consolidados',
      },
      {
        en: 'Vans, pickups and light-duty trucks',
        es: 'Vans, pickups y camiones ligeros',
      },
    ],
    featured: false,
  },
]

export const featuredServices = services.filter((service) => service.featured)
